/**
 * カスタムエラークラス
 * アプリケーション全体で使用する統一されたエラー処理
 */

/**
 * ベースとなるアプリケーションエラー
 */
class ApplicationError extends Error {
  public cause?: unknown;

  constructor(
    message: string,
    public readonly code?: string,
    public readonly context?: Record<string, unknown>,
    options?: ErrorOptions
  ) {
    super(message);
    this.name = 'ApplicationError';
    if (options?.cause) {
      this.cause = options.cause;
    }
    Object.setPrototypeOf(this, ApplicationError.prototype);
  }
}

/**
 * PDF生成関連のエラー
 */
export class PdfGenerationError extends ApplicationError {
  constructor(message: string, context?: Record<string, unknown>, options?: ErrorOptions) {
    super(message, 'PDF_GENERATION_ERROR', context, options);
    this.name = 'PdfGenerationError';
    Object.setPrototypeOf(this, PdfGenerationError.prototype);
  }
}

/**
 * ネットワーク関連のエラー
 */
export class NetworkError extends ApplicationError {
  constructor(
    message: string,
    public readonly statusCode?: number,
    context?: Record<string, unknown>,
    options?: ErrorOptions
  ) {
    super(message, 'NETWORK_ERROR', context, options);
    this.name = 'NetworkError';
    Object.setPrototypeOf(this, NetworkError.prototype);
  }
}

/**
 * 認証関連のエラー
 */
export class AuthenticationError extends ApplicationError {
  constructor(message: string, context?: Record<string, unknown>, options?: ErrorOptions) {
    super(message, 'AUTHENTICATION_ERROR', context, options);
    this.name = 'AuthenticationError';
    Object.setPrototypeOf(this, AuthenticationError.prototype);
  }
}

/**
 * ストレージ関連のエラー
 */
export class StorageError extends ApplicationError {
  constructor(message: string, context?: Record<string, unknown>, options?: ErrorOptions) {
    super(message, 'STORAGE_ERROR', context, options);
    this.name = 'StorageError';
    Object.setPrototypeOf(this, StorageError.prototype);
  }
}

/**
 * エラーが特定のカスタムエラー型かどうかを判定
 */
function isApplicationError(error: unknown): error is ApplicationError {
  return error instanceof ApplicationError;
}

function _isPdfGenerationError(error: unknown): error is PdfGenerationError {
  return error instanceof PdfGenerationError;
}

function _isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}

function _isAuthenticationError(error: unknown): error is AuthenticationError {
  return error instanceof AuthenticationError;
}

/**
 * エラーメッセージを安全に取得
 */
function _getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'Unknown error occurred';
}

/**
 * エラーコンテキストを安全に取得
 */
function _getErrorContext(error: unknown): Record<string, unknown> | undefined {
  if (isApplicationError(error)) {
    return error.context;
  }
  return undefined;
}
